import { useMemo, useCallback, useEffect } from 'react';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';
import { ColumnDef } from '@tanstack/react-table';
import { Comparator, SortOrder, FilterOperator } from '@/lib/utils';
import { TimeRangePicker, TimeRange } from '@/components/ui/time-range-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CalendarIcon } from 'lucide-react';
import {
  useInboundWebhooksQuery,
  EnumWebhookType,
  InboundWebhookSearchDto,
} from '@/lib/api/system/inbound';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
  DataTableFilterBar,
} from '@/components/data-table';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  FilterForm,
  FilterField,
  FilterHeader,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import { cn } from '@/lib/utils';

interface InboundWebhookTableData {
  id: string;
  type: EnumWebhookType;
  keywords: string;
  failure: string;
  received: Date;
  processedAt: Date;
}

function getStatusBadgeProps(status: string) {
  if (!status) {
    return {
      className: 'bg-gray-500',
      children: 'Unknown',
    };
  }

  const statusLower = status.toLowerCase();

  if (
    statusLower.includes('out of working schedule') ||
    statusLower.includes('event skipped') ||
    statusLower.includes('in working schedule processed') ||
    statusLower.includes('site disarmed')
  ) {
    return {
      className: 'bg-green-500',
      children: status,
    };
  } else if (
    statusLower.includes('no snapshots provided with event') ||
    statusLower.includes('attachment not found')
  ) {
    return {
      className: 'bg-orange-500',
      children: status,
    };
  } else if (
    statusLower.includes('no video processor config found') ||
    statusLower.includes('not configured')
  ) {
    return {
      className: 'bg-red-500',
      children: status,
    };
  } else {
    return {
      className: 'bg-gray-500',
      children: status,
    };
  }
}

const inboundWebhookColumns: ColumnDef<InboundWebhookTableData>[] = [
  {
    id: 'failure',
    accessorKey: 'failure',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => {
      const { className, children } = getStatusBadgeProps(row.original.failure);
      return (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold text-white',
            className
          )}
        >
          {children}
        </span>
      );
    },
  },
  {
    id: 'keywords',
    accessorKey: 'keywords',
    header: 'Keywords',
    enableSorting: false,
    cell: ({ row }) => row.original.keywords || '--',
  },
  {
    id: 'received',
    accessorKey: 'received',
    header: 'Received',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.received ? new Date(row.original.received).toLocaleString() : '--',
  },
  {
    id: 'processedAt',
    accessorKey: 'processedAt',
    header: 'Processed',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.processedAt ? new Date(row.original.processedAt).toLocaleString() : '--',
  },
];

function extractInboundWebhookTableData(
  webhooks: InboundWebhookSearchDto[]
): InboundWebhookTableData[] {
  return webhooks.map((webhook) => ({
    id: webhook.id || '',
    type: webhook.type || EnumWebhookType.Umbo,
    keywords: webhook.keywords || '',
    failure: webhook.failure || '',
    received: webhook.received || new Date(),
    processedAt: webhook.processedAt || new Date(),
  }));
}

// Parse time range from URL params
function parseTimeRangeFromParams(searchParams: URLSearchParams): TimeRange | undefined {
  const start = searchParams.get('start');
  const end = searchParams.get('end');

  if (start && end) {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
      return { start: startDate, end: endDate };
    }
  }

  return undefined;
}

function getDefaultLastMinutesRange(minutes: number): TimeRange {
  const end = new Date();
  const start = new Date(end.getTime() - minutes * 60 * 1000);
  return { start, end };
}

export function Inbound() {
  const [urlParams, setUrlParams] = useQueryStates(
    {
      start: parseAsString,
      end: parseAsString,
      keywords: parseAsString,
      page: parseAsInteger.withDefault(1),
      pageSize: parseAsInteger.withDefault(20),
      type: parseAsString,
      failure: parseAsString,
      id: parseAsString,
      receivedFrom: parseAsString,
      receivedTo: parseAsString,
      filters: parseAsString,
    },
    { history: 'replace' }
  );

  const filtersOpen = urlParams.filters === 'open';
  const page = urlParams.page;
  const pageSize = urlParams.pageSize;
  const keywords = urlParams.keywords ?? '';
  const filterType = urlParams.type || undefined;
  const filterFailure = urlParams.failure || undefined;
  const filterId = urlParams.id || undefined;
  const filterReceivedFrom = urlParams.receivedFrom || undefined;
  const filterReceivedTo = urlParams.receivedTo || undefined;

  const timeRange = useMemo(() => {
    if (urlParams.start && urlParams.end) {
      return parseTimeRangeFromParams(
        new URLSearchParams({ start: urlParams.start, end: urlParams.end })
      );
    }
    return undefined;
  }, [urlParams.start, urlParams.end]);

  useEffect(() => {
    if (!urlParams.start || !urlParams.end) {
      const defaultRange = getDefaultLastMinutesRange(15);
      setUrlParams({
        start: defaultRange.start.toISOString(),
        end: defaultRange.end.toISOString(),
      });
    }
  }, [urlParams.start, urlParams.end, setUrlParams]);

  const searchParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      sortBy: 'received',
      sortDir: SortOrder.Descending,
      keywords: keywords || undefined,
      type: (filterType as EnumWebhookType | '') || undefined,
      failure: filterFailure || undefined,
      id: filterId || undefined,
      receivedFrom: filterReceivedFrom || timeRange?.start?.toISOString(),
      receivedTo: filterReceivedTo || timeRange?.end?.toISOString(),
    }),
    [
      page,
      pageSize,
      keywords,
      filterType,
      filterFailure,
      filterId,
      filterReceivedFrom,
      filterReceivedTo,
      timeRange,
    ]
  );

  const { data, isLoading, error } = useInboundWebhooksQuery(searchParams, {
    enabled: !!timeRange,
    queryKey: ['inbound-webhooks', searchParams],
  });

  const webhooks = data?.rows || [];
  const totalCount = data?.totalRows;

  const webhookData = useMemo(() => {
    if (!webhooks) return [];
    return extractInboundWebhookTableData(webhooks);
  }, [webhooks]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setUrlParams({ pageSize: newPageSize, page: 1 });
    },
    [setUrlParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setUrlParams({ page: newPage });
    },
    [setUrlParams]
  );

  const handleTimeRangeChange = useCallback(
    (range: TimeRange | undefined) => {
      if (!range) {
        setUrlParams({ start: null, end: null, page: 1 });
        return;
      }
      setUrlParams({
        start: range.start.toISOString(),
        end: range.end.toISOString(),
        page: 1,
      });
    },
    [setUrlParams]
  );

  const filterCount = useMemo(() => {
    return [filterType, filterFailure, filterId, filterReceivedFrom, filterReceivedTo].filter(
      (v) => v !== undefined && v !== ''
    ).length;
  }, [filterType, filterFailure, filterId, filterReceivedFrom, filterReceivedTo]);

  const showPrompt = !timeRange;
  const tableLoading = isLoading && !!timeRange;

  return (
    <div className="flex flex-col gap-4">

      {showPrompt ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="mt-4 text-center text-gray-400">
            <CalendarIcon className="mx-auto mb-4 h-12 w-12 opacity-50" />
            <h2 className="mb-2 text-lg font-medium text-white">Select a Time Range</h2>
            <p className="text-sm">
              Choose a time range of up to 1 hour within the last 7 days, then click Search to view
              inbound traffic.
            </p>
          </div>
        </div>
      ) : (
        <FilterForm
          defaultValues={{
            type: filterType,
            failure: filterFailure,
            id: filterId,
            receivedFrom: filterReceivedFrom ? new Date(filterReceivedFrom) : undefined,
            receivedTo: filterReceivedTo ? new Date(filterReceivedTo) : undefined,
          }}
          onFiltersChange={({ values }) => {
            setUrlParams({
              type: (values.type as string) || null,
              failure: (values.failure as string) || null,
              id: (values.id as string) || null,
              receivedFrom:
                values.receivedFrom instanceof Date ? values.receivedFrom.toISOString() : null,
              receivedTo:
                values.receivedTo instanceof Date ? values.receivedTo.toISOString() : null,
              page: 1,
            });
          }}
        >
          <DataTable
            columns={inboundWebhookColumns}
            data={webhookData}
            loading={tableLoading}
            error={error as Error | null}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 100]}
            stateKey="inbound-webhooks-table"
            emptyMessage="No inbound webhooks found for the selected time range"
            sidebarOpen={filtersOpen}
            onSidebarOpenChange={(open) => setUrlParams({ filters: open ? 'open' : null })}
          >
            <DataTableContainer className="w-full items-stretch md:flex-nowrap md:gap-4">
              <DataTableFilterBar className="w-full shrink-0 md:w-[320px] md:flex-none md:shrink-0">
                <div className="flex min-h-0 flex-col">
                  <FilterHeader onClose={() => setUrlParams({ filters: null })} />
                  <ScrollArea className="flex-1 px-4 pb-6">
                    <div className="space-y-4 pb-4 pt-2">
                      <FilterInlineResetButton
                        onReset={() => {
                          setUrlParams({
                            type: null,
                            failure: null,
                            id: null,
                            receivedFrom: null,
                            receivedTo: null,
                            page: 1,
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-4 pb-4">
                      <FilterField
                        type="toggle"
                        name="type"
                        label="Type"
                        field="type"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Any', value: '' },
                          ...Object.values(EnumWebhookType).map((t) => ({
                            label: t,
                            value: t,
                          })),
                        ]}
                      />
                      <FilterField
                        type="text"
                        name="failure"
                        label="Status"
                        field="failure"
                        comparator={Comparator.ILike}
                        placeholder="Contains..."
                      />
                      <FilterField
                        type="text"
                        name="id"
                        label="Webhook ID"
                        field="id"
                        comparator={Comparator.ILike}
                        placeholder="ID..."
                      />
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-400">Received window</Label>
                        <div className="grid grid-cols-1 gap-3">
                          <FilterField
                            type="date"
                            name="receivedFrom"
                            label=""
                            field="received"
                            comparator={Comparator.GtEq}
                            placeholder="From"
                          />
                          <FilterField
                            type="date"
                            name="receivedTo"
                            label=""
                            field="received"
                            comparator={Comparator.LtEq}
                            placeholder="To"
                          />
                        </div>
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              </DataTableFilterBar>
              <DataTableContent className="w-full min-w-0 flex-1">
                <DataTableUtilityBar
                  showColumnVisibility
                  showFilterToggle
                  search={{
                    value: keywords,
                    onChange: (value) =>
                      setUrlParams({
                        keywords: value || null,
                        page: 1,
                      }),
                    placeholder: 'Search keywords...',
                  }}
                  filterCount={filterCount}
                  actions={
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-400 whitespace-nowrap">Time Range:</Label>
                        <TimeRangePicker
                          value={timeRange}
                          onChange={handleTimeRangeChange}
                          maxDurationHours={1}
                          maxAgeDays={7}
                          className="max-w-max"
                        />
                      </div>
                    </div>
                  }
                />
                <DataTableWrapper>
                  <DataTableHeader />
                  <DataTableRows />
                </DataTableWrapper>
                <DataTableFooter />
              </DataTableContent>
            </DataTableContainer>
          </DataTable>
        </FilterForm>
      )}
    </div>
  );
}

export default Inbound;
